import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { submitMessage, getContact } from '../services/api';

const Contact: React.FC = () => {
  const [contact, setContact] = useState({
    phone: '+234 800 NIDAVE',
    email: 'info@nidave.com',
    address: "La' Goshen Building, Oluyole Industrial Estate, Ibadan",
    hours: 'Mon - Fri: 8am - 5pm',
    agency: 'sublet@nidave.com'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [form, setForm]           = useState({ 
    name:'', email:'', phone:'', subject:'', message:'' 
  });
    
  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Fetch contact info from backend ──────────────────────────────────────
  useEffect(() => {
    getContact()
      .then(r => {
        // Handle both { data: { phone, email, … } } and { data: { contact: {…} } } shapes
        const payload = r?.data?.contact ?? r?.data;
        if (payload?.phone) setContact(payload);
      })
      .catch(() => {/* fallback to default state values */});
  }, []);

  // ── Submit message to backend ─────────────────────────────────────────────
  const submit = async () => {
    if (!form.name || !form.email || !form.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitMessage(form);
      setSubmitted(true);
    } catch (err: any) {
      // Show a real error rather than silently succeeding
      const msg = err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-py" id="contact">
      <div className="container">
        <div className="section-label">Get In Touch</div>
        <h2 className="section-title">Contact Us</h2>
        <div className="contact-grid">

          {/* ── Left column: contact info ── */}
          <div>
            <p style={{ color:'var(--text-secondary)', lineHeight:'1.8', marginBottom:'32px' }}>
              Ready to discuss your next infrastructure project? Reach out through any of the channels below.
            </p>
            {[
              { icon:<Phone size={18}/>,  label:'Phone',         value: contact.phone   },
              { icon:<Mail size={18}/>,   label:'Email',         value: contact.email   },
              { icon:<MapPin size={18}/>, label:'Address',       value: contact.address },
              { icon:<Clock size={18}/>,  label:'Working Hours', value: contact.hours   },
            ].map(item => (
              <div className="contact-info-item" key={item.label}>
                <div className="contact-info-icon">{item.icon}</div>
                <div>
                  <div className="contact-info-label">{item.label}</div>
                  <div className="contact-info-value">{item.value}</div>
                </div>
              </div>
            ))}

            <div style={{ marginTop:'28px', padding:'22px', background:'linear-gradient(135deg,var(--brand-orange),var(--brand-orange-light))', borderRadius:'10px', color:'white' }}>
              <div style={{ fontFamily:'var(--font-condensed)', fontSize:'0.76rem', fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', opacity:0.8, marginBottom:'8px' }}>Housing Inquiries</div>
              <p style={{ fontSize:'0.88rem', lineHeight:'1.65', opacity:0.9 }}>
                For sublet and leasing enquiries, contact our Housing Agency at <strong>{contact.agency}</strong>
              </p>
            </div>
          </div>

          {/* ── Right column: form ── */}
          <div>
            {submitted ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'320px', textAlign:'center', gap:'16px' }}>
                <CheckCircle size={64} style={{ color:'#40916c' }} />
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'2rem', color:'var(--text-primary)' }}>Message Sent!</h3>
                <p style={{ color:'var(--text-secondary)', maxWidth:'300px', lineHeight:'1.7' }}>Our team will get back to you within 24 hours.</p>
                <button
                  className="btn-outline"
                  onClick={() => { setSubmitted(false); setForm({ name:'', email:'', phone:'', subject:'', message:'' }); }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <div className="contact-form">
                {error && (
                  <div style={{ padding:'12px 16px', background:'rgba(220,53,69,0.08)', border:'1px solid rgba(220,53,69,0.2)', borderRadius:'6px', color:'#dc3545', fontFamily:'var(--font-condensed)', fontSize:'0.88rem' }}>
                    {error}
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input className="form-input" name="name" value={form.name} onChange={change} placeholder="John Doe" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-input" name="email" type="email" value={form.email} onChange={change} placeholder="john@co.com" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" name="phone" value={form.phone} onChange={change} placeholder="+234 800 000 0000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <select className="form-select" name="subject" value={form.subject} onChange={change}>
                      <option value="">Select subject</option>
                      <option>General Inquiry</option>
                      <option>Project Quote</option>
                      <option>Sublet / Leasing</option>
                      <option>Partnership</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea className="form-textarea" name="message" value={form.message} onChange={change} placeholder="Tell us about your project or inquiry..." />
                </div>
                <button
                  className="btn-primary"
                  onClick={submit}
                  disabled={loading}
                  style={{ alignSelf:'flex-start' }}
                >
                  {loading ? 'Sending…' : <><Send size={15}/> Send Message</>}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;